import type { PlacementPayload, Poster, PosterUpdatePayload, ScanEvent } from "./types";

export interface RuntimeConfig {
  firebase: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
  };
  accountId: string;
  publicBaseUrl: string;
}

let authTokenProvider: (() => Promise<string | undefined>) | undefined;

export function setAuthTokenProvider(provider: () => Promise<string | undefined>) {
  authTokenProvider = provider;
}

async function headers() {
  const token = await authTokenProvider?.();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

export async function getRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch("/api/config");
  if (!response.ok) throw new Error("Unable to load runtime config");
  return response.json();
}

export async function getPosters(): Promise<Poster[]> {
  const response = await fetch("/api/posters", { headers: await headers() });
  if (!response.ok) throw new Error("Unable to load posters");
  const body = await response.json();
  return body.posters;
}

export async function getScans(): Promise<ScanEvent[]> {
  const response = await fetch("/api/scans", { headers: await headers() });
  if (!response.ok) throw new Error("Unable to load scan logs");
  const body = await response.json();
  return body.scans;
}

export async function getPosterScans(posterId: string): Promise<ScanEvent[]> {
  const response = await fetch(`/api/posters/${encodeURIComponent(posterId)}/scans`, { headers: await headers() });
  if (!response.ok) throw new Error("Unable to load poster scan logs");
  const body = await response.json();
  return body.scans;
}

export async function savePlacement(payload: PlacementPayload): Promise<Poster> {
  const response = await fetch("/api/placements", {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Unable to save placement");
  }
  const body = await response.json();
  return body.poster;
}

export async function clearPlacement(posterId: string): Promise<Poster> {
  const response = await fetch(`/api/placements/${encodeURIComponent(posterId)}`, {
    method: "DELETE",
    headers: await headers()
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Unable to clear placement");
  }
  const body = await response.json();
  return body.poster;
}

export async function updatePoster(posterId: string, payload: PosterUpdatePayload): Promise<Poster> {
  const response = await fetch(`/api/posters/${encodeURIComponent(posterId)}`, {
    method: "PATCH",
    headers: await headers(),
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Unable to update poster");
  }
  const body = await response.json();
  return body.poster;
}

export async function resetPosterScans(posterId: string): Promise<Poster> {
  const response = await fetch(`/api/posters/${encodeURIComponent(posterId)}/reset-scans`, {
    method: "POST",
    headers: await headers()
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Unable to reset scans");
  }
  const body = await response.json();
  return body.poster;
}

export function postersToCsv(posters: Poster[]) {
  const columns = [
    "poster_id",
    "design_type",
    "batch_id",
    "is_placed",
    "placement_timestamp",
    "placement_lat",
    "placement_lng",
    "neighborhood",
    "venue_type",
    "venue",
    "scan_count",
    "last_scan_at",
    "notes",
    "photo_url"
  ];

  const rows = posters.map((poster) =>
    [
      poster.posterId,
      poster.designType,
      poster.batchId,
      poster.isPlaced,
      poster.placementTimestamp ?? "",
      poster.placementLat ?? "",
      poster.placementLng ?? "",
      poster.tags?.neighborhood ?? "",
      poster.tags?.venueType ?? "",
      poster.tags?.venue ?? "",
      poster.scanCount,
      poster.lastScanAt ?? "",
      poster.notes ?? "",
      poster.photoUrl ?? ""
    ]
      .map((value) => `"${String(value).replaceAll('"', '""')}"`)
      .join(",")
  );

  return [columns.join(","), ...rows].join("\n");
}

export function scansToCsv(scans: ScanEvent[]) {
  const columns = ["scan_id", "poster_id", "design_type", "scanned_at", "referrer", "user_agent", "ip_hash", "redirect_url", "ignored_for_analytics"];
  const rows = scans.map((scan) =>
    [
      scan.id,
      scan.posterId,
      scan.designType ?? "",
      scan.scannedAt,
      scan.referrer ?? "",
      scan.userAgent ?? "",
      scan.ipHash ?? "",
      scan.redirectUrl,
      scan.ignoredForAnalytics ?? false
    ]
      .map((value) => `"${String(value).replaceAll('"', '""')}"`)
      .join(",")
  );
  return [columns.join(","), ...rows].join("\n");
}
